import type {
  Reporter,
  FullConfig,
  Suite,
  TestCase,
  TestResult,
  TestStep,
  FullResult,
  TestError,
} from '@playwright/test/reporter';
import { randomUUID } from 'crypto';
import { readFileSync, writeFileSync, existsSync } from 'fs';
import { readFile, stat } from 'fs/promises';
import { basename } from 'path';
import { HttpClient } from '../streaming/http.js';
import {
  decideTestControls,
  type TestControlsDecision,
  type FinishedTest,
  type QuarantineFetchOutcome,
} from './test-controls.js';
import { KafkaEventProducer } from '../streaming/kafka.js';

import { EventBuffer } from '../streaming/buffer.js';
import { EventDeliveryManager } from '../streaming/delivery.js';
import { createMetadataCollector } from '../metadata/index.js';
import type {
  TestdinoConfig,
  TestEvent,
  TestRunBeginEvent,
  TestBeginEvent,
  TestStepBeginEvent,
  TestStepEndEvent,
  TestEndEvent,
  TestRunEndEvent,
  TestRunErrorEvent,
  TestConsoleOutEvent,
  TestConsoleErrEvent,
  RunHeartbeatEvent,
  RunMetadata,
  Annotation,
} from '../types/index.js';
import { DEFAULT_SERVER_URL } from '../types/index.js';
import {
  ArtifactUploadClient,
  ArtifactUploadTokenError,
  isAllowedExtension,
  isWithinSizeLimit,
  type UploadFileRequest,
  type UploadTokenResult,
} from '../uploads/index.js';
import { createReporterLog, type ReporterLog, type RunSummaryData } from './log.js';
import { symbols } from '../utils/ui.js';
import {
  ForbiddenAuthError,
  HttpDeliveryError,
  isBlockedRunError,
  isQuotaError,
  InvalidTokenTypeError,
  PartialBatchError,
  ServerEndpointError,
  UnauthorizedError,
  isPermanentHttpStatus,
} from './errors.js';
import { isDebugEnabled, normalizePath, sleep, waitAtMost, withTimeout } from '../utils/index.js';
import { awaitUpdateResult, checkForUpdate, getPackageVersion } from '../utils/update-notifier.js';
import { HEARTBEAT_POLL_INTERVAL_MS, HEARTBEAT_SILENCE_THRESHOLD_MS } from './heartbeat.js';

// Maximum console chunk size (10KB in characters)
const MAX_CONSOLE_CHUNK_SIZE = 10_000;

// Events per flush. Sized so one in-flight request covers what arrives during its
// round trip (rate x RTT): a fast suite emits ~3,900 events/s, so a smaller cap
// drains slower than events arrive and the buffer grows for the whole run.
// Measured against the real endpoint at 3,937 events/s over a 50ms RTT: a 10-event
// cap stranded 31,780 of 39,000 events; 500 delivered all of them, peak depth 507.
const MAX_BUFFER_SIZE = 500;

// Byte ceiling per flush — the binding limit when events are heavy (large errors),
// because Event Hubs rejects any single message over 1 MiB. Whichever cap trips first.
const MAX_BUFFER_BYTES = 1_000_000;

// Periodic flush interval (ms) — ensures events are sent even when the buffer isn't
// full. This bounds latency on slow suites, so the size cap above costs them nothing.
const FLUSH_INTERVAL_MS = 250;

// Maximum consecutive delivery failures before discarding events (prevents unbounded buffer growth)
const MAX_DELIVERY_FAILURES = 3;

// How long onEnd waits for a Test Controls list still in flight (the request
// itself times out at 5 s); past it the build behaves as without Test Controls.
const QUARANTINE_END_WAIT_MS = 3000;

// Runs inside init, which the buffer's onFlush awaits — the whole budget is
// time the first events cannot be delivered.
const RUN_LINK_MAX_ATTEMPTS = 3;
const RUN_LINK_RETRY_DELAY_MS = 400;
const RUN_LINK_ATTEMPT_TIMEOUT_MS = 1000;
const RUN_LINK_TOTAL_BUDGET_MS = 3000;

// Internal intermediate representations threaded through the uploadAttachments
// pipeline (statAttachments → filterAllowedExtensions → putBatch). Kept close
// to the class since no other module needs them.
type StatedAttachment =
  | { name: string; contentType: string; path: string; sizeBytes: number }
  | { name: string; contentType: string; unavailable: true };

type UploadableAttachment = { name: string; contentType: string; path: string; sizeBytes: number };

export class TestdinoReporter implements Reporter {
  private config: TestdinoConfig;
  private buffer: EventBuffer | null = null;
  private runId: string;
  private sequenceNumber = 0;

  private shardInfo?: { current: number; total: number };
  // Manual-split correlation, reaching the reporter via the CLI temp-config like shardInfo (not this.config). splitId = group key; splitInfo = position.
  private splitInfo?: { current: number; total: number };
  private splitId?: string;
  // injectedRunId replaces the fresh UUID so the dispatcher ack and this reporter's
  // run:begin/end carry the same identity (the manifest contract).
  private swarmId?: string;
  private injectedRunId?: string;
  // This machine's id under swarm; emitted on run:begin so ingestion stamps
  // test_cases.machine_id for the per-machine breakdown.
  private machineId?: string;
  private rerunOf?: string;
  private runStartTime?: number;

  private sigintHandler?: () => void;
  private sigtermHandler?: () => void;
  private isShuttingDown = false;
  private shutdownResolve: (() => void) | null = null;
  private shutdownPromise: Promise<void> | null = null;

  private deliveryFailureCount = 0;

  private quotaExceeded = false;
  private pendingQuotaError: unknown = null;
  // Set after run:begin is delivered when auth reports the project over its
  // execution quota: suppress all further streaming without failing the build.
  private overLimitBlocked = false;

  private httpClient: HttpClient | null = null;
  private runUrlPromise: Promise<string | undefined> | null = null;
  private resolvedRunUrl: string | undefined;
  private resolvedTestRunId: string | undefined;
  private projectId: string | null = null;

  private kafkaProducer: KafkaEventProducer | null = null;

  private deliveryManager: EventDeliveryManager | null = null;

  // Constructed at init when enabled; no network call happens until the first
  // test:end that has file attachments. Upload-token issuance is per-test.
  private artifactUploadClient: ArtifactUploadClient | null = null;
  private artifactsEnabled = true; // Default: enabled

  // Deferred initialization - resolves true on success, false on failure
  private initPromise: Promise<boolean> | null = null;
  private initFailed = false;

  // Test Controls list, fetched while tests run; onEnd waits a little longer.
  private quarantineFetch: Promise<QuarantineFetchOutcome> | null = null;
  // Counted even when init failed: an error outside a test must never go green.
  private globalErrorCount = 0;
  private rootSuite: Suite | null = null;

  // Promises for onTestEnd; must be awaited in onEnd to prevent data loss
  private pendingTestEndPromises: Set<Promise<void>> = new Set();
  private openAttempts = new Set<string>();
  private openAttemptsFinalized = false;

  // Started in onBegin so the registry round trip overlaps the run; read in onEnd
  private updateCheckPromise: Promise<string | undefined> | null = null;

  private artifactCounts = { screenshots: 0, videos: 0, traces: 0, other: 0, unavailable: 0 };

  // Logger for consistent output
  private log: ReporterLog;

  private testCounts = { passed: 0, failed: 0, skipped: 0, timedOut: 0, interrupted: 0, flaky: 0, retried: 0 };
  private totalTests = 0;
  // Incremented ahead of the init guard so a batch that ran during a TestDino
  // outage (initFailed) still counts and stays ack-eligible.
  private executedTests = 0;
  private beganEmpty = false;
  private inBeginReplay = false;
  private failedBeforeBegin = false;
  private summaryPrinted = false;

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  private projectNames = new Set<string>();
  private runMetadata: RunMetadata | null = null;
  private rootDir = '';
  private workerCount = 0;

  constructor(config: TestdinoConfig = {}) {
    const cliConfig = this.loadCliConfig();

    this.config = { ...config, ...cliConfig };

    this.runId = this.injectedRunId ?? randomUUID();

    this.log = createReporterLog({ debug: this.config.debug ?? false });

    this.artifactsEnabled = this.config.artifacts !== false;

    this.buffer = new EventBuffer({
      maxSize: MAX_BUFFER_SIZE,
      maxBytes: MAX_BUFFER_BYTES,
      flushIntervalMs: FLUSH_INTERVAL_MS,
      logger: this.log,
      onFlush: async (events) => {
        if (this.initPromise) {
          const success = await this.initPromise;
          if (!success) return; // Init failed - discard events
        }
        // Events buffered before overLimitBlocked was set (init runs unawaited)
        // must be dropped here too — the hook guards only stop future calls.
        if (this.overLimitBlocked) return;
        await this.sendEvents(events);
      },
    });
  }

  private loadCliConfig(): TestdinoConfig {
    const cliConfigPath = process.env.TESTDINO_CLI_CONFIG_PATH;

    if (!cliConfigPath) {
      return {};
    }

    try {
      if (!existsSync(cliConfigPath)) {
        return {};
      }

      const configContent = readFileSync(cliConfigPath, 'utf-8');
      const cliConfig = JSON.parse(configContent) as Record<string, unknown>;

      const mappedConfig: TestdinoConfig = {};

      if (cliConfig.token !== undefined && typeof cliConfig.token === 'string') {
        mappedConfig.token = cliConfig.token;
      }

      if (cliConfig.serverUrl !== undefined && typeof cliConfig.serverUrl === 'string') {
        mappedConfig.serverUrl = cliConfig.serverUrl;
      }

      if (cliConfig.debug !== undefined && typeof cliConfig.debug === 'boolean') {
        mappedConfig.debug = cliConfig.debug;
      }

      // CI run ID for grouping sharded runs
      if (cliConfig.ciRunId !== undefined && typeof cliConfig.ciRunId === 'string') {
        mappedConfig.ciRunId = cliConfig.ciRunId;
      }

      // Handle artifacts flag
      if (cliConfig.artifacts !== undefined && typeof cliConfig.artifacts === 'boolean') {
        mappedConfig.artifacts = cliConfig.artifacts;
      }

      // Without this, --tags / TESTDINO_TAGS / --rerun-tags resolve in the CLI and never reach run:begin.
      if (Array.isArray(cliConfig.tags)) {
        const tags = cliConfig.tags.filter((tag): tag is string => typeof tag === 'string');
        if (tags.length > 0) {
          mappedConfig.tags = tags;
        }
      }

      // Split correlation — CLI-only, stored outside this.config like shardInfo.
      const rawSplit = cliConfig.split;
      if (rawSplit !== null && typeof rawSplit === 'object') {
        const { current, total } = rawSplit as { current?: unknown; total?: unknown };
        if (typeof current === 'number' && typeof total === 'number') {
          this.splitInfo = { current, total };
        }
      }
      if (typeof cliConfig.splitId === 'string' && cliConfig.splitId.length > 0) {
        this.splitId = cliConfig.splitId;
      }
      if (typeof cliConfig.rerunOf === 'string' && cliConfig.rerunOf.length > 0) {
        this.rerunOf = cliConfig.rerunOf;
      }
      // A narrowed in-run re-run executes --shard=1/1, so Playwright's own shard
      // no longer identifies the job; the CLI sends the real one here.
      const rawShard = cliConfig.shard;
      if (rawShard !== null && typeof rawShard === 'object') {
        const { current, total } = rawShard as { current?: unknown; total?: unknown };
        if (typeof current === 'number' && typeof total === 'number') {
          this.shardInfo = { current, total };
        }
      }

      // Swarm correlation — CLI-only, stored outside this.config like split.
      if (typeof cliConfig.swarmId === 'string' && cliConfig.swarmId.length > 0) {
        this.swarmId = cliConfig.swarmId;
      }
      if (typeof cliConfig.runId === 'string' && cliConfig.runId.length > 0) {
        this.injectedRunId = cliConfig.runId;
      }
      if (typeof cliConfig.machineId === 'string' && cliConfig.machineId.length > 0) {
        this.machineId = cliConfig.machineId;
      }
      // Swarm is the definitive grouping mode. A co-present split id (which
      // the CLI validator already rejects) must never diverge the reporter rail from
      // the dispatcher's completion manifest — swarm wins, split is dropped.
      if (this.swarmId) {
        this.splitId = undefined;
        this.splitInfo = undefined;
      }
      return mappedConfig;
    } catch (error) {
      // Silently fail - CLI config is optional
      if (isDebugEnabled()) {
        console.warn(
          '⚠️  TestDino: Failed to load CLI config:',
          error instanceof Error ? error.message : String(error)
        );
      }
      return {};
    }
  }

  // Wire fields for manual-split mode: splitId top-level, split nested (mirrors
  // shard). Both present or neither, so events never carry a half-set pair.
  private splitFields(): { splitId: string; split: { current: number; total: number } } | undefined {
    return this.splitId && this.splitInfo ? { splitId: this.splitId, split: this.splitInfo } : undefined;
  }

  // swarmId is top-level (no declared total); splitId is dropped in
  // loadCliConfig when it is set, so the two never co-emit.
  private orchFields(): { swarmId: string; machineId?: string } | undefined {
    if (!this.swarmId) return undefined;
    return {
      swarmId: this.swarmId,
      ...(this.machineId ? { machineId: this.machineId } : {}),
    };
  }

  async onBegin(config: FullConfig, suite: Suite): Promise<void> {
    // When global setup or test loading fails, Playwright calls onBegin itself
    // from onEnd with an empty root suite, then synchronously replays the errors
    // it deferred. Only errors inside that replay predate the first test; a
    // teardown error arrives later. Recorded before any early return.
    this.beganEmpty = suite?.allTests?.().length === 0;
    this.inBeginReplay = true;
    queueMicrotask(() => {
      this.inBeginReplay = false;
    });

    // Check if we're a duplicate instance (CLI-injected when already in config)
    if (config && this.isDuplicateInstance(config.reporter)) {
      if (this.config.debug) {
        this.log.debug('Reporter already configured in playwright.config, skipping duplicate instance');
      }
      return;
    }

    this.rootDir = config?.rootDir ?? '';
    this.rootSuite = suite ?? null;
    const token = this.getToken();

    if (!token) {
      this.printConfigurationError('Token is required but not provided', [
        'Set environment variable: export TESTDINO_TOKEN=your-token',
        'In CI, store it as a secret named TESTDINO_TOKEN and pass it to the test step',
        'Read it in playwright.config.ts: token: process.env.TESTDINO_TOKEN',
        'Use CLI wrapper: npx tdpw test --token your-token',
      ]);
      return;
    }

    // Store shard info and run start time for potential interruption handling
    // Already set when the CLI sent the job's real shard (in-run re-run).
    if (config?.shard && !this.shardInfo) {
      this.shardInfo = {
        current: config.shard.current,
        total: config.shard.total,
      };
    }
    this.runStartTime = Date.now();

    // Under `tdpw test` the CLI prints this notice itself; checking here too would print it twice.
    if (!process.env.TESTDINO_CLI_CONFIG_PATH) {
      this.updateCheckPromise = checkForUpdate(getPackageVersion());
    }

    // Kick off async initialization without awaiting - Playwright does not await onBegin. Events will be buffered and transmitted once init completes.
    this.initPromise = this.performAsyncInit(config, suite, token);
  }

  private async performAsyncInit(config: FullConfig, suite: Suite, token: string): Promise<boolean> {
    const serverUrl = this.getBaseServerUrl();

    try {
      // Register signal handlers early so SIGINT during init still cleans up
      this.registerSignalHandlers();

      // Authenticate with the TestDino server
      this.httpClient = new HttpClient({ token, serverUrl });
      const auth = await this.httpClient.authenticate();
      this.projectId = auth.projectId;
      this.log.success('Authenticated successfully');
      if (!auth.overLimit) {
        this.log.info('TestDino Test Controls: fetching rules…');
        this.quarantineFetch = this.httpClient.fetchQuarantineList() ?? null;
      }

      // Initialize artifact upload client if enabled. Under the new
      // data-handler contract there is NO network call at init — upload
      // tokens are minted per-test at test:end. Constructing the client is
      // free.
      if (this.artifactsEnabled) {
        this.artifactUploadClient = new ArtifactUploadClient({
          token,
          serverUrl: this.getBaseServerUrl(),
          runId: this.runId,
          onDebug: (message) => this.log.debug(message),
        });
        this.log.debug('Artifact upload client ready (upload tokens minted per-test at test:end)');
      } else {
        this.log.info('Artifact upload disabled (artifacts: false) — screenshots, videos, and traces stay local');
      }

      // Collect metadata
      const metadata = await this.collectMetadata(config, suite);
      this.runMetadata = metadata;
      this.workerCount = config.workers;

      // Build run:begin event — sent directly (not via buffer) to guarantee
      // it is the first event. initPromise resolves only after delivery,
      // so buffer's onFlush will transmit buffered events after run:begin.
      const beginEvent: TestRunBeginEvent = {
        type: 'run:begin',
        runId: this.runId,
        projectId: this.projectId!,
        metadata,
        ciRunId: this.config.ciRunId,
        shard: this.shardInfo,
        ...this.splitFields(),
        ...this.orchFields(),
        ...(this.config.tags && this.config.tags.length > 0 && { tags: this.config.tags }),
        ...(this.rerunOf && { rerunOf: this.rerunOf }),
        timestamp: Date.now(),
        sequence: 0,
      };

      this.log.debug(`Run ${this.runId.slice(0, 8)} — ${metadata.skeleton?.totalTests ?? 0} tests`);

      // Connect to Kafka using config from auth response
      let kafkaConnected = false;
      if (auth.kafka) {
        try {
          if (!auth.kafka.channel) {
            throw new Error('Server did not assign a Kafka channel (contract violation)');
          }
          this.kafkaProducer = new KafkaEventProducer({
            brokers: auth.kafka.brokers,
            channel: auth.kafka.channel,
            clientId: `testdino-reporter-${this.runId.slice(0, 8)}`,
            logger: this.log,
            ...(auth.kafka.sasl && { sasl: auth.kafka.sasl }),
            ...(auth.kafka.ssl !== undefined && { ssl: auth.kafka.ssl }),
          });
          await this.kafkaProducer.connect();
          kafkaConnected = true;
        } catch (kafkaError) {
          this.log.warn(
            `Kafka connection failed: ${kafkaError instanceof Error ? kafkaError.message : String(kafkaError)}. ` +
              'Falling back to HTTP delivery.'
          );
          await this.kafkaProducer?.close();
          this.kafkaProducer = null;
        }
      }

      if (kafkaConnected && this.kafkaProducer) {
        if (!auth.httpFallback) {
          this.log.warn('Server omitted httpFallback URL — kafka-only mode, no recovery on kafka failure');
        }
        this.deliveryManager = new EventDeliveryManager({
          kafkaProducer: this.kafkaProducer,
          httpClient: this.httpClient!,
          ...(auth.httpFallback && { httpFallbackUrl: auth.httpFallback }),
          logger: this.log,
        });
      } else if (auth.httpFallback) {
        this.deliveryManager = new EventDeliveryManager({
          httpClient: this.httpClient!,
          httpFallbackUrl: auth.httpFallback,
          logger: this.log,
        });
      }

      // Send run:begin via delivery manager (Kafka primary + HTTP fallback, or HTTP-only)
      if (this.deliveryManager) {
        // Set before the send: over HTTP, ingestion answers a blocked run:begin
        // with a 402 and the catch below must already know the run is blocked.
        if (auth.overLimit) this.overLimitBlocked = true;
        this.log.debug(
          kafkaConnected ? 'Delivery channel: kafka (primary) + http (fallback)' : 'Delivery channel: http only'
        );
        await this.deliveryManager.send(beginEvent);
        this.log.success(kafkaConnected ? 'run:begin delivered' : 'run:begin delivered (HTTP fallback)');

        // Over limit: run:begin was delivered (so ingestion surfaces the blocked
        // run), but stop before the run URL + heartbeat so nothing else streams.
        if (this.overLimitBlocked) {
          this.log.warn(
            'Over execution limit — this run will not be recorded on TestDino. Tests will continue to run.'
          );
          return true;
        }

        // Not backgrounded: the list reporter erases the preceding line to
        // repaint, wiping anything printed once it starts rendering. Every shard
        // announces its own — separate processes, often separate machines.
        this.resolvedRunUrl = await this.resolveRunUrl();
        this.runUrlPromise = Promise.resolve(this.resolvedRunUrl);
        const startLink = this.sliceScopedUrl(this.resolvedRunUrl);
        // Under swarm each batch is a separate reporter process and none knows the
        // merged run's link; the dashboard is the place to watch a swarm run.
        if (startLink && !this.swarmId) this.log.runLink(startLink);

        // Start heartbeat monitor now that delivery channel is ready
        this.startHeartbeatMonitor();
      }

      return true;
    } catch (error) {
      // Ingestion refused the run:begin as over limit (HTTP path): a blocked run,
      // not a reporter failure, so the build stays green and the notice prints.
      if (isBlockedRunError(error) || (this.overLimitBlocked && isQuotaError(error))) {
        this.overLimitBlocked = true;
        this.log.warn('Over execution limit — this run will not be recorded on TestDino. Tests will continue to run.');
        return true;
      }
      this.initFailed = true;
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (isQuotaError(error)) {
        this.quotaExceeded = true;
        this.pendingQuotaError = error;
      } else if (error instanceof InvalidTokenTypeError) {
        this.printConfigurationError('Invalid token type', [
          error.message,
          'Generate a pipeline API key (td_api_...) from your TestDino project settings',
          `Server URL: ${serverUrl}`,
        ]);
      } else if (error instanceof ForbiddenAuthError) {
        this.printConfigurationError('Authentication forbidden', [
          error.message,
          'Verify your project has access to this server',
          `Server URL: ${serverUrl}`,
        ]);
      } else if (error instanceof ServerEndpointError) {
        this.printConfigurationError('Wrong server URL', [
          error.message,
          'Check serverUrl in your config (CLI > testdino.config > playwright.config > env)',
        ]);
      } else if (error instanceof UnauthorizedError) {
        this.printConfigurationError('Authentication failed - Invalid or expired token', [
          'Verify your token is correct',
          'Check if the token has expired',
          'Generate a new token from TestDino dashboard',
          `Server URL: ${serverUrl}`,
        ]);
      } else {
        this.printConfigurationError(`Failed to initialize TestDino reporter: ${errorMessage}`, [
          'Check if TestDino server is running and accessible',
          `Verify server URL is correct: ${serverUrl}`,
          'Check network connectivity',
          'Review server logs for details',
        ]);
      }
      this.log.error(`Failed to initialize reporter: ${errorMessage}`);
      return false;
    }
  }

  async onTestBegin(test: TestCase, result: TestResult): Promise<void> {
    if (!this.initPromise || this.initFailed || this.overLimitBlocked) return;

    const key = this.attemptKey(test, result);
    this.openAttempts.add(key);

    const event: TestBeginEvent = {
      type: 'test:begin',
      runId: this.runId,
      ...this.getEventMetadata(),

      // Test identification
      testId: test.id,
      title: test.title,
      titlePath: test.titlePath(),

      // Location information (normalized to repo-relative)
      location: {
        file: this.normalizeFilePath(test.location.file),
        line: test.location.line,
        column: test.location.column,
      },

      // Test configuration
      tags: test.tags,
      expectedStatus: test.expectedStatus,
      timeout: test.timeout,
      retries: test.retries,
      annotations: this.extractAnnotations(test.annotations),

      // Execution context
      retry: result.retry,
      workerIndex: result.workerIndex,
      parallelIndex: result.parallelIndex,
      repeatEachIndex: test.repeatEachIndex,

      // Hierarchy information
      parentSuite: this.extractParentSuite(test.parent),

      // Timing
      startTime: result.startTime.getTime(),
    };

    try {
      await this.buffer!.add(event);
    } catch {
      this.openAttempts.delete(key);
    }
  }

  private attemptKey(test: TestCase, result: Pick<TestResult, 'retry'>): string {
    return `${test.id}:${result.retry}`;
  }

  private didNotStart(status: FullResult['status']): boolean {
    return this.beganEmpty && this.failedBeforeBegin && status !== 'passed';
  }

  private finalizeOpenAttemptsAsInterrupted(): void {
    if (this.openAttemptsFinalized) return;
    this.openAttemptsFinalized = true;
    const n = this.openAttempts.size;
    if (n === 0) return;
    this.testCounts.interrupted += n;
    this.totalTests += n;
    // These attempts began (onTestBegin fired) but never reached onTestEnd, so bump the
    // ack-gate count here too — an interrupted-but-started batch genuinely ran.
    this.executedTests += n;
    this.openAttempts.clear();
  }

  async onStepBegin(test: TestCase, result: TestResult, step: TestStep): Promise<void> {
    if (!this.initPromise || this.initFailed || this.overLimitBlocked) return;

    const event: TestStepBeginEvent = {
      type: 'step:begin',
      runId: this.runId,
      ...this.getEventMetadata(),

      testId: test.id,
      stepId: `${test.id}-${step.titlePath().join('-')}`,
      title: step.title,
      titlePath: step.titlePath(),

      category: step.category,

      // Location Information (normalized to repo-relative)
      location: step.location
        ? {
            file: this.normalizeFilePath(step.location.file),
            line: step.location.line,
            column: step.location.column,
          }
        : undefined,

      parentStep: step.parent ? this.extractParentStep(step.parent) : undefined,

      // Timing
      startTime: step.startTime.getTime(),

      retry: result.retry,

      workerIndex: result.workerIndex,
      parallelIndex: result.parallelIndex,

      // Annotations
      annotations: this.extractAnnotations(step.annotations),
    };

    await this.buffer!.add(event);
  }

  async onStepEnd(test: TestCase, result: TestResult, step: TestStep): Promise<void> {
    if (!this.initPromise || this.initFailed || this.overLimitBlocked) return;

    // Derive status from error presence
    const status: 'passed' | 'failed' = step.error ? 'failed' : 'passed';

    const event: TestStepEndEvent = {
      type: 'step:end',
      runId: this.runId,
      ...this.getEventMetadata(),

      testId: test.id,
      stepId: `${test.id}-${step.titlePath().join('-')}`,
      title: step.title,
      titlePath: step.titlePath(),

      // Timing
      duration: step.duration,

      error: this.extractError(step.error),

      // Status Information
      status,

      // Child Steps Summary
      childSteps: this.extractChildSteps(step),

      attachments: this.extractAttachments(step),

      // Annotations
      annotations: this.extractAnnotations(step.annotations),

      retry: result.retry,

      workerIndex: result.workerIndex,
      parallelIndex: result.parallelIndex,
    };

    await this.buffer!.add(event);
  }

  onTestEnd(test: TestCase, result: TestResult): void {
    // Ack-gate count first — before the init guard — so a batch that genuinely ran
    // during a TestDino outage (initFailed) is still recorded as having produced results.
    this.executedTests++;

    if (!this.initPromise || this.initFailed || this.overLimitBlocked) return;

    this.openAttempts.delete(this.attemptKey(test, result));

    // Track project name
    const projectName = this.getProjectName(test);
    if (projectName) {
      this.projectNames.add(projectName);
    }

    if (!this.openAttemptsFinalized) {
      // Track test result counts for summary table display
      if (result.retry > 0) {
        this.testCounts.retried++;
      }
      // Playwright only retries an `unexpected` outcome, so anything else is
      // already the last attempt. Keying on status dropped every test whose
      // final attempt was failed or skipped below the retry ceiling, and
      // counted a passing attempt once per retry.
      const isFinalAttempt =
        result.status === 'interrupted' || test.outcome() !== 'unexpected' || result.retry === test.retries;
      if (isFinalAttempt) {
        this.totalTests++;
        // Bucket on Playwright's verdict: `status` describes one attempt and
        // disagrees with it for a `test.fail()` test.
        const outcome = test.outcome();
        if (outcome === 'flaky') {
          this.testCounts.flaky++;
        } else if (outcome === 'skipped') {
          this.testCounts.skipped++;
        } else if (outcome === 'expected') {
          this.testCounts.passed++;
        } else if (result.status === 'timedOut') {
          this.testCounts.timedOut++;
        } else if (result.status === 'interrupted') {
          this.testCounts.interrupted++;
        } else {
          this.testCounts.failed++;
        }
      }
    }

    // Create and track the async work - Playwright does not await onTestEnd
    const workPromise = this.processTestEnd(test, result);
    this.pendingTestEndPromises.add(workPromise);

    // Remove from tracking when complete (success or failure)
    workPromise.finally(() => {
      this.pendingTestEndPromises.delete(workPromise);
    });
  }

  private waitForShutdown(): Promise<void> {
    if (this.isShuttingDown) return Promise.resolve();
    if (!this.shutdownPromise) {
      this.shutdownPromise = new Promise((resolve) => {
        this.shutdownResolve = resolve;
      });
    }
    return this.shutdownPromise;
  }

  private async processTestEnd(test: TestCase, result: TestResult): Promise<void> {
    try {
      let attachmentsWithUrls: TestEndEvent['attachments'];
      if (this.isShuttingDown) {
        attachmentsWithUrls = this.attachmentsAsUnavailable(result.attachments);
      } else {
        attachmentsWithUrls = await Promise.race([
          this.uploadAttachments(result.attachments, test.id),
          this.waitForShutdown().then(() => this.attachmentsAsUnavailable(result.attachments)),
        ]);
        if (this.isShuttingDown) {
          attachmentsWithUrls = this.attachmentsAsUnavailable(result.attachments);
        } else {
          this.tallyArtifacts(attachmentsWithUrls);
        }
      }

      const event: TestEndEvent = {
        type: 'test:end',
        runId: this.runId,
        ...this.getEventMetadata(),

        testId: test.id,

        // Status Information
        status: result.status as 'passed' | 'failed' | 'skipped' | 'timedOut' | 'interrupted',
        outcome: test.outcome(),

        // Timing
        duration: result.duration,

        // Execution Context
        retry: result.retry,

        workerIndex: result.workerIndex,
        parallelIndex: result.parallelIndex,

        annotations: this.extractAnnotations(result.annotations),

        errors: result.errors
          .map((e) => this.extractError(e))
          .filter((e): e is NonNullable<typeof e> => e !== undefined),

        steps: this.extractTestStepsSummary(result),

        // Attachments Metadata (with Azure URLs when uploaded)
        attachments: attachmentsWithUrls,

        stdout: result.stdout.length > 0 ? this.extractConsoleOutput(result.stdout) : undefined,
        stderr: result.stderr.length > 0 ? this.extractConsoleOutput(result.stderr) : undefined,
      };

      this.buffer!.enqueue(event);
    } catch (error) {
      // Log error but don't throw - one test failure shouldn't break others
      this.log.error(`Failed to process test:end event: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async onEnd(result: FullResult): Promise<{ status?: FullResult['status'] } | void> {
    // Written FIRST, before any early return, so the spawner can tell an empty batch
    // from one that ran even during a TestDino outage; a write failure never fails the build.
    const resultPath = process.env.TESTDINO_RUN_RESULT_PATH;
    if (resultPath) {
      try {
        writeFileSync(resultPath, JSON.stringify({ totalTests: this.executedTests }));
      } catch {
        // reporter-isolation: never let a sentinel write break the customer run.
      }
    }

    if (!this.initPromise) {
      await this.printUpdateNoticeIfAvailable();
      return;
    }

    // Await init before reading quotaExceeded — the init catch sets it async
    // and onEnd can fire before it lands.
    const success = await this.initPromise;

    if (this.overLimitBlocked) {
      this.stopHeartbeatMonitor();
      if (this.pendingTestEndPromises.size > 0) {
        await Promise.allSettled(Array.from(this.pendingTestEndPromises));
      }
      this.printOverLimitNotice();
      this.log.success('Tests completed — this run was not recorded on TestDino (over execution limit)');
      this.buffer?.destroy();
      await this.kafkaProducer?.close();
      await this.printUpdateNoticeIfAvailable();
      this.removeSignalHandlers();
      // Return void, never { status: 'failed' } — an over-limit run must not fail
      // the customer's build; Playwright's own exit status stands.
      return;
    }

    if (this.quotaExceeded) {
      this.stopHeartbeatMonitor();
      if (this.pendingTestEndPromises.size > 0) {
        await Promise.allSettled(Array.from(this.pendingTestEndPromises));
      }
      if (this.pendingQuotaError) {
        this.printQuotaError(this.pendingQuotaError);
      }
      this.log.success('Tests completed (quota limit reached; not streamed to TestDino)');
      this.buffer?.destroy();
      await this.kafkaProducer?.close();
      await this.printUpdateNoticeIfAvailable();
      this.removeSignalHandlers();
      return { status: 'failed' };
    }

    if (!success) {
      // don't fire after resources are torn down (floating promise prevention)
      if (this.pendingTestEndPromises.size > 0) {
        await Promise.allSettled(Array.from(this.pendingTestEndPromises));
      }
      this.buffer?.destroy();
      this.pendingTestEndPromises.clear();
      await this.kafkaProducer?.close();
      await this.printUpdateNoticeIfAvailable();
      this.removeSignalHandlers();
      return;
    }

    // Stop heartbeat monitor — run is ending, no more heartbeats needed
    this.stopHeartbeatMonitor();

    // Await completion of all pending test:end events before flushing final events
    if (this.pendingTestEndPromises.size > 0) {
      this.log.debug(`Waiting for ${this.pendingTestEndPromises.size} pending test:end events...`);
      // Use Promise.allSettled for error isolation - one failed test shouldn't block others
      await Promise.allSettled(Array.from(this.pendingTestEndPromises));
    }

    const testControls = await this.decideTestControls(result);

    const event: TestRunEndEvent = {
      type: 'run:end',
      runId: this.runId,
      ...this.getEventMetadata(),

      // The test results' status, never Test Controls' decision: a run with a
      // failed test is failed even when quarantine passed the build.
      status: result.status as 'passed' | 'failed' | 'timedout' | 'interrupted',

      // Timing
      duration: result.duration,
      startTime: result.startTime.getTime(),

      shard: this.shardInfo,
      ...this.splitFields(),
      ...this.orchFields(),

      quarantine: testControls.block,

      executedTests: this.totalTests + this.openAttempts.size,
      ...(this.didNotStart(result.status) && { notStarted: true as const }),
    };

    // Stop the periodic flush timer before the final explicit flush
    this.buffer?.destroy();

    // Flush any remaining buffered events first (retry once — doFlush restores events on failure)
    try {
      await this.buffer?.flush();
    } catch (error) {
      this.log.warn(`Failed to flush buffered events, retrying: ${error}`);
      try {
        await this.buffer?.flush();
      } catch (retryError) {
        this.log.error(`Retry flush also failed, some events may be lost: ${retryError}`);
      }
    }

    // Deliver run:end via delivery manager (Kafka primary, HTTP fallback)
    // Skip if SIGINT handler is already sending an interruption event
    if (this.deliveryManager && !this.isShuttingDown) {
      try {
        await this.deliveryManager.send(event);
        this.log.success('run:end delivered');
      } catch (error) {
        this.log.error(`Failed to deliver run:end event: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    // Print comprehensive run summary
    if (!this.summaryPrinted) {
      this.summaryPrinted = true;
      const summaryData: RunSummaryData = {
        runId: this.resolvedTestRunId ?? this.runId,
        runMetadata: this.runMetadata,
        testCounts: this.testCounts,
        artifactCounts: this.artifactCounts,
        totalTests: this.totalTests,
        workerCount: this.workerCount,
        projectNames: this.projectNames,
        shardInfo: this.shardInfo,
        artifactsDisabled: !this.artifactsEnabled,
        // Never re-resolves: a second loop would add its timeout budget to shutdown.
        runUrl: this.sliceScopedUrl(this.resolvedRunUrl ?? (await this.runUrlPromise) ?? undefined),
      };
      this.log.printRunSummary(result, summaryData);
    }
    for (const line of testControls.report) this.log[line.tone](line.text);

    // initFailed excluded: a TestDino outage during init must not fail the
    // customer's build (reporter-isolation). Quota is handled by the early
    // return above.
    const discardedEvents = this.deliveryManager?.discardCount ?? 0;
    const deliveryFailed = discardedEvents > 0 && !this.initFailed;

    if (deliveryFailed) {
      this.printDeliveryDiscardBanner(discardedEvents);
    }

    // Close connections and remove signal handlers
    if (!this.isShuttingDown) {
      await this.kafkaProducer?.close();
      await this.printUpdateNoticeIfAvailable();
      this.removeSignalHandlers();
    }

    if (deliveryFailed) return { status: 'failed' };
    if (testControls.status) return { status: testControls.status };
  }

  private async decideTestControls(result: FullResult): Promise<TestControlsDecision> {
    const fetch = this.quarantineFetch
      ? await waitAtMost(this.quarantineFetch, QUARANTINE_END_WAIT_MS, { reason: 'timeout' } as const)
      : ({ reason: 'unreachable' } as const);
    return decideTestControls({
      fetch,
      tests: this.finishedTests(),
      runStatus: result.status,
      globalErrorCount: this.globalErrorCount,
    });
  }

  private finishedTests(): FinishedTest[] {
    return (this.rootSuite?.allTests() ?? []).map((test) => {
      const last = test.results[test.results.length - 1];
      return {
        id: test.id,
        title: test.title,
        outcome: test.outcome(),
        errorMessage: last?.error?.message ?? last?.errors?.[0]?.message ?? '',
      };
    });
  }

  private printDeliveryDiscardBanner(count: number): void {
    const border = '═'.repeat(70);
    console.error('');
    console.error(border);
    console.error('  ⚠️  TestDino Reporter dropped events — run marked FAILED');
    console.error(border);
    console.error(`  ${count} event${count === 1 ? '' : 's'} were not delivered to TestDino.`);
    console.error('');
    console.error('  The dashboard for this run will show incomplete data.');
    console.error('  CI exit code is non-zero so this cannot be misread as a passing build.');
    console.error(border);
    console.error('');
  }

  async onError(error: TestError): Promise<void> {
    this.globalErrorCount++;
    if (this.inBeginReplay) this.failedBeforeBegin = true;
    if (!this.initPromise || this.initFailed || this.overLimitBlocked) return;

    const event: TestRunErrorEvent = {
      type: 'run:error',
      runId: this.runId,
      ...this.getEventMetadata(),

      // Error Information (with cause support)
      error: this.extractGlobalError(error),
    };

    await this.buffer!.add(event);
  }

  async onStdOut(chunk: string | Buffer, test?: TestCase, result?: TestResult): Promise<void> {
    if (!this.initPromise || this.initFailed || this.overLimitBlocked) return;

    const { text, truncated } = this.truncateChunk(chunk);

    const event: TestConsoleOutEvent = {
      type: 'console:out',
      runId: this.runId,
      ...this.getEventMetadata(),

      text,

      // Test Association (optional)
      testId: test?.id,
      retry: result?.retry,

      // Truncation Indicator
      truncated,
    };

    await this.buffer!.add(event);
  }

  async onStdErr(chunk: string | Buffer, test?: TestCase, result?: TestResult): Promise<void> {
    if (!this.initPromise || this.initFailed || this.overLimitBlocked) return;

    const { text, truncated } = this.truncateChunk(chunk);

    const event: TestConsoleErrEvent = {
      type: 'console:err',
      runId: this.runId,
      ...this.getEventMetadata(),

      // Console Error Output
      text,

      // Test Association (optional)
      testId: test?.id,
      retry: result?.retry,

      // Truncation Indicator
      truncated,
    };

    await this.buffer!.add(event);
  }

  printsToStdio(): boolean {
    return false;
  }

  private async sendEvents(events: TestEvent[]): Promise<void> {
    if (events.length === 0) return;

    // Deliver via delivery manager (Kafka primary, HTTP fallback with retry)
    // Debug logging happens at the delivery layer (batch-level, not per-event)
    if (this.deliveryManager) {
      try {
        await this.deliveryManager.sendBatch(events);
        this.deliveryFailureCount = 0;
      } catch (error) {
        if (error instanceof HttpDeliveryError && isPermanentHttpStatus(error.status)) {
          this.log.error(`Permanent delivery error (HTTP ${error.status}) — discarding ${events.length} events`);
          this.deliveryManager.recordDiscard(events.length);
          return;
        }
        if (isBlockedRunError(error)) {
          this.log.warn(
            'Over execution limit — this run will not be recorded on TestDino. Tests will continue to run.'
          );
          this.overLimitBlocked = true;
          // Init already started the heartbeat for this run; ingestion refused it.
          this.stopHeartbeatMonitor();
          this.deliveryManager.recordDiscard(events.length);
          return;
        }
        // Quota errors bypass HttpDeliveryError, so the permanent-status branch
        // above misses them. Set quotaExceeded so onEnd's early return fires.
        if (isQuotaError(error)) {
          this.log.error(`Permanent delivery error (402 quota) — discarding ${events.length} events`);
          this.quotaExceeded = true;
          this.pendingQuotaError = error;
          this.deliveryManager.recordDiscard(events.length);
          return;
        }

        // Partial-batch → some sub-batches shipped; reset the consecutive-failure
        // counter (a peer succeeding is progress).
        if (error instanceof PartialBatchError) {
          this.deliveryFailureCount = 0;
          throw error;
        }

        this.deliveryFailureCount++;
        this.log.error(`Failed to deliver events: ${error instanceof Error ? error.message : String(error)}`);

        if (this.deliveryFailureCount <= MAX_DELIVERY_FAILURES) {
          throw error; // Let buffer restore events for retry on next flush
        }
        // Sustained outage — discard to prevent unbounded buffer growth
        this.log.warn(
          `Delivery failed ${MAX_DELIVERY_FAILURES} consecutive times — discarding events to prevent memory growth`
        );
        this.deliveryManager.recordDiscard(events.length);
        this.deliveryFailureCount = 0;
      }
    } else {
      this.log.debug(`No delivery manager — ${events.length} events dropped`);
    }
  }

  private getToken(): string | undefined {
    return this.config.token || process.env.TESTDINO_TOKEN;
  }

  private getBaseServerUrl(): string {
    return this.config.serverUrl || process.env.TESTDINO_SERVER_URL || DEFAULT_SERVER_URL;
  }

  private printConfigurationError(message: string, solutions: string[]): void {
    const border = '═'.repeat(70);
    console.error('');
    console.error(border);
    console.error(`  ${symbols.error} TestDino Reporter Configuration Error`);
    console.error(border);
    console.error(`  ${message}`);
    console.error('');
    console.error('  Solutions:');
    solutions.forEach((solution, index) => {
      console.error(`    ${index + 1}. ${solution}`);
    });
    console.error(border);
    console.error('');
  }

  private async collectMetadata(playwrightConfig: FullConfig, playwrightSuite: Suite): Promise<RunMetadata> {
    try {
      const metadataCollector = createMetadataCollector(playwrightConfig, playwrightSuite);
      const result = await metadataCollector.collectAll();

      if (result.failureCount > 0) {
        this.log.warn(`${result.failureCount}/${result.results.length} metadata collectors failed`);
      }

      // Build skeleton from suite
      const skeleton = metadataCollector.buildSkeleton(playwrightSuite);

      // The collector reads Playwright's own shard, which a narrowed in-run
      // re-run has replaced with 1/1; shardInfo holds the job's real one, and
      // it is what groups an attempt's jobs into a single run.
      const playwright = this.shardInfo
        ? { ...result.metadata.playwright, shard: this.shardInfo }
        : result.metadata.playwright;

      // Add skeleton to metadata
      return {
        ...result.metadata,
        ...(playwright && { playwright }),
        skeleton,
      };
    } catch (error) {
      this.log.warn(`Metadata collection failed: ${error instanceof Error ? error.message : String(error)}`);
      return {}; // Return empty metadata as fallback
    }
  }

  private normalizeFilePath(filePath: string): string {
    return normalizePath(filePath, this.rootDir);
  }

  private getEventMetadata() {
    return {
      timestamp: Date.now(),
      sequence: ++this.sequenceNumber,
    };
  }

  private extractAnnotations(annotations: Array<Annotation> | undefined): Array<Annotation> {
    if (!annotations || annotations.length === 0) {
      return [];
    }

    return annotations.map((a) => ({
      type: a.type,
      description: a.description,
    }));
  }

  private extractParentSuite(parent: Suite): TestBeginEvent['parentSuite'] {
    return {
      title: parent.title,
      type: parent.type,
      location: parent.location
        ? {
            file: this.normalizeFilePath(parent.location.file),
            line: parent.location.line,
            column: parent.location.column,
          }
        : undefined,
    };
  }

  private extractParentStep(parent: TestStep): TestStepBeginEvent['parentStep'] {
    return {
      title: parent.title,
      category: parent.category,
      location: parent.location
        ? {
            file: this.normalizeFilePath(parent.location.file),
            line: parent.location.line,
            column: parent.location.column,
          }
        : undefined,
    };
  }

  private extractChildSteps(step: TestStep): TestStepEndEvent['childSteps'] {
    return {
      count: step.steps.length,
      steps: step.steps.map((child) => ({
        title: child.title,
        status: child.error ? 'failed' : 'passed',
      })),
    };
  }

  private extractError(error: TestError | undefined): TestStepEndEvent['error'] {
    if (!error) return undefined;

    return {
      message: error.message || String(error),
      stack: error.stack,
      snippet: error.snippet,
      value: error.value,
      location: error.location
        ? {
            file: this.normalizeFilePath(error.location.file),
            line: error.location.line,
            column: error.location.column,
          }
        : undefined,
    };
  }

  private extractGlobalError(error: TestError): TestRunErrorEvent['error'] {
    return {
      message: error.message ?? String(error.value ?? 'Unknown error'),
      stack: error.stack,
      snippet: error.snippet,
      value: error.value,
      location: error.location
        ? {
            file: this.normalizeFilePath(error.location.file),
            line: error.location.line,
            column: error.location.column,
          }
        : undefined,
      // Handle nested error cause (v1.49+)
      cause: error.cause
        ? {
            message: error.cause.message,
            stack: error.cause.stack,
            snippet: error.cause.snippet,
            value: error.cause.value,
            location: error.cause.location
              ? {
                  file: this.normalizeFilePath(error.cause.location.file),
                  line: error.cause.location.line,
                  column: error.cause.location.column,
                }
              : undefined,
          }
        : undefined,
    };
  }

  // Advisory, non-failing notice — deliberately worded to NOT collide with
  // printQuotaError's build-failing "Execution Limit Reached" banner, and on
  // console.warn (a heads-up, not an error). The overLimit flag carries no quota
  // detail payload (plan, reset date, usage), so there is nothing to render from.
  private printOverLimitNotice(): void {
    const border = '═'.repeat(70);
    console.warn('');
    console.warn(border);
    console.warn('  ⚠️  TestDino: this run was not recorded');
    console.warn(border);
    console.warn('');
    console.warn('  Your organization has reached its execution limit.');
    console.warn('  This run was not saved to TestDino and will not appear later.');
    console.warn('  Your tests and CI build status are unaffected.');
    console.warn('');
    console.warn('  To resume recording runs, upgrade your plan, or on Pro and Team');
    console.warn('  plans an org admin can add executions for this billing cycle');
    console.warn('  in Settings → Billing & Usage.');
    console.warn(border);
    console.warn('');
  }

  private printQuotaError(error: unknown): void {
    const border = '═'.repeat(70);
    const errorData = error as Record<string, unknown>;
    const details = errorData.details as Record<string, unknown>;
    const planName = (details?.planName as string) || 'Unknown';
    const resetDate = details?.resetDate;
    const formattedResetDate = resetDate
      ? new Date(resetDate as string).toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : undefined;

    console.error('');
    console.error(border);

    if (errorData.code === 'QUOTA_EXCEEDED') {
      const exceeded = details as {
        total?: number;
        used?: number;
        remaining?: number;
        totalTests?: number;
        projectName?: string;
        projectLimit?: number;
        projectUsed?: number;
        projectBorrowed?: number;
      };

      // Always calculate remaining from total - used (don't trust server's remaining field)
      const orgRemaining = (exceeded.total ?? 0) - (exceeded.used ?? 0);
      const effectiveLimit = (exceeded.projectLimit ?? 0) + (exceeded.projectBorrowed ?? 0);

      console.error(`  ${symbols.error} TestDino Project Execution Limit Reached`);
      console.error(border);
      console.error('');
      console.error('  The test case limit allocated to this project has been exceeded.');
      console.error('');
      console.error('  Project Usage:');
      if (exceeded.projectName) {
        console.error(`    Project:          ${exceeded.projectName}`);
      }
      if (exceeded.projectLimit != null) {
        console.error(
          `    Limit:            ${effectiveLimit} test cases${exceeded.projectBorrowed ? ` (${exceeded.projectLimit} allocated + ${exceeded.projectBorrowed} borrowed)` : ''}`
        );
      }
      if (exceeded.projectUsed != null) {
        console.error(`    Used:             ${exceeded.projectUsed}`);
      }
      console.error(`    This run:         ${exceeded.totalTests || 'Unknown'} test cases`);
      console.error('    Status:           Project quota exhausted');
      console.error('');
      console.error('  Organization Usage:');
      console.error(`    Plan:             ${planName} (${exceeded.total || 'Unknown'} test cases / month)`);
      console.error(`    Used:             ${exceeded.used || 'Unknown'}`);
      console.error(`    Remaining:        ${orgRemaining}`);
      if (orgRemaining > 0) {
        console.error('');
        console.error('  Note:');
        console.error(`  Your organization still has ${orgRemaining} test cases available,`);
        console.error('  but they are not allocated to this project.');
      }
      console.error('');
      console.error('  Solutions:');
      console.error('    1. Allocate more test case quota to this project in');
      console.error('       Settings → Billing & Usage → Test Limits');
      console.error('    2. Enable Auto Allocation to automatically distribute');
      console.error('       remaining organization quota');
    } else if (errorData.code === 'QUOTA_EXHAUSTED') {
      console.error(`  ${symbols.error} TestDino Organization Execution Limit Reached`);
      console.error(border);
      console.error('');
      console.error('  Your organization has exhausted its monthly test case limit.');
      console.error('');
      console.error('  Organization Usage:');
      console.error(`    Plan:             ${planName} (${details.totalLimit || 'Unknown'} test cases / month)`);
      console.error(`    Used:             ${details.used || 'Unknown'}`);
      console.error('    Remaining:        0');
      console.error('');
      console.error('  Solutions:');
      console.error('    1. Upgrade your plan, or on Pro and Team plans an org admin can');
      console.error('       add executions for this billing cycle in Settings → Billing & Usage');
    }

    if (formattedResetDate) {
      console.error('');
      console.error('  Monthly Reset:');
      console.error(`    ${formattedResetDate}`);
    }

    console.error('');
    console.error('  Docs:    https://docs.testdino.com/platform/billing-and-usage/test-limits');
    console.error('  Pricing: https://testdino.com/pricing');
    console.error('');
    console.error('  CI exit code is non-zero — this run may be missing data in TestDino.');
    console.error(border);
    console.error('');
  }

  private truncateChunk(chunk: string | Buffer): { text: string; truncated?: boolean } {
    // Convert Buffer to string (UTF-8)
    let convertedText: string;
    if (Buffer.isBuffer(chunk)) {
      convertedText = chunk.toString('utf-8');
    } else {
      convertedText = chunk;
    }

    // Check if truncation is needed
    if (convertedText.length > MAX_CONSOLE_CHUNK_SIZE) {
      return {
        text: convertedText.substring(0, MAX_CONSOLE_CHUNK_SIZE) + '\n[truncated]',
        truncated: true,
      };
    }

    return { text: convertedText };
  }

  private extractAttachments(step: TestStep): TestStepEndEvent['attachments'] {
    // step:end never carries a path — uploads happen once, in bulk, at
    // test:end. Emitting the local path here would leak the reporter's
    return step.attachments.map((a) => ({
      name: a.name,
      contentType: a.contentType,
    }));
  }

  private extractTestStepsSummary(result: TestResult): TestEndEvent['steps'] {
    return {
      total: result.steps.length,
      passed: result.steps.filter((s) => !s.error).length,
      failed: result.steps.filter((s) => s.error).length,
    };
  }

  private extractConsoleOutput(output: Array<string | Buffer>): string[] {
    return output.map((item) => (typeof item === 'string' ? item : item.toString()));
  }

  private isDuplicateInstance(reporters: unknown[]): boolean {
    const count = this.countTestdinoReporters(reporters);

    // If count > 1, it means we have both:
    // - One from playwright.config (user's manual config)
    // - One from CLI injection (our --reporter flag)
    // The config-based instance should handle reporting, so we exit
    return count > 1;
  }

  private countTestdinoReporters(reporters: unknown): number {
    // Guard against undefined or non-array reporters
    if (!reporters || !Array.isArray(reporters)) {
      return 0;
    }

    let count = 0;

    for (const reporter of reporters) {
      // Reporters can be in multiple formats:
      // - ['@testdino/playwright'] - string in array
      // - ['@testdino/playwright', options] - string with options
      // - Class instance (when instantiated with new)

      if (Array.isArray(reporter) && reporter.length > 0) {
        const reporterName = reporter[0];
        if (this.isTestdinoReporter(reporterName)) {
          count++;
        }
      } else if (reporter instanceof TestdinoReporter) {
        count++;
      }
    }

    return count;
  }

  private isTestdinoReporter(value: unknown): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    // Check various forms our reporter might appear as:
    // - '@testdino/playwright' (npm package name)
    // - 'TestdinoReporter' (class name)
    // - Path ending with 'testdino-playwright'
    return (
      value.includes('@testdino/playwright') ||
      value.includes('TestdinoReporter') ||
      value.endsWith('testdino-playwright')
    );
  }

  private startHeartbeatMonitor(): void {
    if (!this.deliveryManager) return;

    // Seed lastDeliveryAt so the first heartbeat waits a full silence window
    this.deliveryManager.lastDeliveryAt = Date.now();

    this.heartbeatTimer = setInterval(() => {
      if (!this.deliveryManager) return;

      const silenceMs = Date.now() - this.deliveryManager.lastDeliveryAt;
      if (silenceMs >= HEARTBEAT_SILENCE_THRESHOLD_MS) {
        const event: RunHeartbeatEvent = {
          type: 'run:heartbeat',
          runId: this.runId,
          timestamp: Date.now(),
        };
        this.log.debug(`Sending heartbeat (${Math.round(silenceMs / 1000)}s silence)`);
        // Fire-and-forget — heartbeat is non-critical, must never block
        void this.deliveryManager.send(event).catch(() => {});
      }
    }, HEARTBEAT_POLL_INTERVAL_MS);

    // Don't let heartbeat timer keep the process alive during shutdown
    this.heartbeatTimer.unref();
  }

  private stopHeartbeatMonitor(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private registerSignalHandlers(): void {
    // Remove any existing handlers first to prevent listener accumulation
    this.removeSignalHandlers();

    let lastSignalTime = 0;
    this.sigintHandler = () => {
      const now = Date.now();
      // Debounce: ignore signals within 500ms of the first (signal propagation noise).
      // A real second Ctrl+C from the user will be >500ms later.
      if (this.isShuttingDown) {
        if (now - lastSignalTime > 500) {
          this.log.error('Force exit');
          process.exit(130);
        }
        return;
      }
      lastSignalTime = now;
      this.handleInterruption('SIGINT', 130);
    };

    this.sigtermHandler = () => {
      if (this.isShuttingDown) return;
      this.handleInterruption('SIGTERM', 143);
    };

    process.on('SIGINT', this.sigintHandler);
    process.on('SIGTERM', this.sigtermHandler);
  }

  private async resolveRunUrl(): Promise<string | undefined> {
    if (!this.httpClient || this.initFailed) return undefined;
    const client = this.httpClient;

    const deadline = Date.now() + RUN_LINK_TOTAL_BUDGET_MS;
    for (let attempt = 0; attempt < RUN_LINK_MAX_ATTEMPTS; attempt++) {
      try {
        // Per-attempt, so one hung call cannot spend the retries' budget.
        const link = await withTimeout(
          client.resolveRunLink(this.runId),
          Math.max(0, Math.min(RUN_LINK_ATTEMPT_TIMEOUT_MS, deadline - Date.now())),
          'run link resolve'
        );
        if (link) {
          this.resolvedTestRunId = link.testRunId;
          return link.url;
        }
      } catch (error) {
        this.log.debug(`Run link unavailable: ${error instanceof Error ? error.message : String(error)}`);
        // Transient errors fall through to a retry: run:begin was just sent, so a
        // cold connection pool lands exactly here.
        if (error instanceof HttpDeliveryError && isPermanentHttpStatus(error.status)) return undefined;
      }
      if (Date.now() + RUN_LINK_RETRY_DELAY_MS >= deadline) break;
      if (attempt < RUN_LINK_MAX_ATTEMPTS - 1) await sleep(RUN_LINK_RETRY_DELAY_MS);
    }
    return undefined;
  }

  // Scope the printed run URL to this process's own slice (split and/or shard),
  // so each CI job links straight to what it ran instead of the whole merged run.
  private sliceScopedUrl(url: string | undefined): string | undefined {
    if (!url) return undefined;
    const params = new URLSearchParams();
    if (this.splitInfo) params.set('split', String(this.splitInfo.current));
    if (this.shardInfo) params.set('shard', String(this.shardInfo.current));
    const query = params.toString();
    return query ? `${url}?${query}` : url;
  }

  private async printUpdateNoticeIfAvailable(): Promise<void> {
    if (!this.updateCheckPromise) return;
    const pending = this.updateCheckPromise;
    this.updateCheckPromise = null;
    try {
      const latestVersion = await awaitUpdateResult(pending);
      if (latestVersion) {
        this.log.printUpdateNotice(getPackageVersion(), latestVersion);
      }
    } catch {
      // A version check must never fail the user's run.
    }
  }

  private removeSignalHandlers(): void {
    if (this.sigintHandler) {
      process.removeListener('SIGINT', this.sigintHandler);
    }
    if (this.sigtermHandler) {
      process.removeListener('SIGTERM', this.sigtermHandler);
    }
  }

  private handleInterruption(signal: string, exitCode: number): void {
    if (this.isShuttingDown) return;
    this.isShuttingDown = true;
    this.shutdownResolve?.();
    this.shutdownResolve = null;

    // Stop periodic flush timer and heartbeat monitor — interruption sends directly
    this.buffer?.destroy();
    this.stopHeartbeatMonitor();

    this.log.warn(`Received ${signal}, sending interruption event...`);

    if (!this.initPromise) {
      // Init never started, just exit
      process.exit(exitCode);
    }

    const waitForPending = async () => {
      const deadline = Date.now() + 2500;
      while (this.pendingTestEndPromises.size > 0 && Date.now() < deadline) {
        try {
          await Promise.race([
            Promise.allSettled(Array.from(this.pendingTestEndPromises)),
            this.timeoutPromise(Math.max(50, Math.min(500, deadline - Date.now())), 'Pending events timeout'),
          ]);
        } catch {
          // Keep draining until empty or deadline.
        }
      }
    };

    // Create interruption event
    const event: TestRunEndEvent = {
      type: 'run:end',
      runId: this.runId,
      ...this.getEventMetadata(),
      status: 'interrupted',
      duration: this.runStartTime ? Date.now() - this.runStartTime : 0,
      startTime: this.runStartTime ?? Date.now(),
      shard: this.shardInfo,
      ...this.splitFields(),
      ...this.orchFields(),
    };

    // Keep event loop alive with interval - CRITICAL for ensuring async operations complete
    const keepAlive = setInterval(() => {
      // Empty interval to keep process alive
    }, 100);

    // Hard timeout to force exit if send hangs.
    // Budget: init 2s + pending 2.5s + flush 1s + emergency 1s + send 2.5s ≈ 9s worst case.
    const forceExitTimer = setTimeout(() => {
      clearInterval(keepAlive);
      this.log.error('Force exit — send timeout exceeded');
      process.exit(exitCode);
    }, 10000);

    // Send interruption event directly (bypass buffer to avoid flush delays)
    const sendAndExit = async () => {
      try {
        // Wait for init to complete so kafkaProducer/deliveryManager are available
        if (this.initPromise) {
          await Promise.race([this.initPromise, this.timeoutPromise(2000, 'Init timeout')]).catch(() => {});
        }

        // Wait briefly for pending test:end events (best effort)
        await waitForPending();

        // Drain buffered events before run:end — destroy() stopped the timer, so this
        // is the only remaining path to deliver events accumulated since the last tick.
        if (this.buffer) {
          await Promise.race([this.buffer.flush(), this.timeoutPromise(1000, 'Buffer flush timeout')]).catch(() => {});
          // Hung in-flight flush holds the lock; leftovers enqueued after it still need a send.
          // drain() (not getEvents()+clear()) so a late enqueue during the send isn't lost or double-sent.
          if (this.buffer.size() > 0 && this.deliveryManager) {
            const leftover = this.buffer.drain();
            try {
              await Promise.race([
                this.deliveryManager.sendUrgent(leftover),
                this.timeoutPromise(1000, 'Emergency drain timeout'),
              ]);
            } catch {
              // Best-effort; run:end still follows.
            }
          }
        }

        // Counted at send time: test:end events drained above are only now in totalTests.
        event.executedTests = this.totalTests + this.openAttempts.size;

        // Send directly without buffering for speed
        await Promise.race([this.sendInterruptionEvent(event), this.timeoutPromise(2500, 'Send timeout')]);

        this.log.success('Interruption event sent');
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        this.log.error(`Failed to send interruption event: ${errorMsg}`);
      } finally {
        this.finalizeOpenAttemptsAsInterrupted();

        if (!this.summaryPrinted) {
          this.summaryPrinted = true;
          const summaryData: RunSummaryData = {
            runId: this.resolvedTestRunId ?? this.runId,
            runMetadata: this.runMetadata,
            testCounts: this.testCounts,
            artifactCounts: this.artifactCounts,
            totalTests: this.totalTests,
            workerCount: this.workerCount,
            projectNames: this.projectNames,
            shardInfo: this.shardInfo,
            artifactsDisabled: !this.artifactsEnabled,
            runUrl: this.sliceScopedUrl(this.resolvedRunUrl),
          };
          this.log.printRunSummary(
            { status: 'interrupted', duration: event.duration, startTime: new Date(event.startTime) } as FullResult,
            summaryData
          );
        }

        clearTimeout(forceExitTimer);
        clearInterval(keepAlive);
        process.exit(exitCode);
      }
    };

    // Execute
    sendAndExit().catch(() => {
      clearTimeout(forceExitTimer);
      clearInterval(keepAlive);
      process.exit(exitCode);
    });
  }

  // Throws so the caller cannot log an undelivered event as sent.
  private async sendInterruptionEvent(event: TestRunEndEvent): Promise<void> {
    if (this.deliveryManager) {
      await this.deliveryManager.sendUrgent([event]);
    } else if (this.kafkaProducer?.isConnected) {
      await this.kafkaProducer.send(event);
      await this.kafkaProducer.close();
    }
  }

  private timeoutPromise(ms: number, message: string): Promise<never> {
    return new Promise((_, reject) => {
      const timer = setTimeout(() => reject(new Error(message)), ms);
      timer.unref?.();
    });
  }

  // Counts are per attempt, not per test: onTestEnd fires once per retry and each
  // attempt uploads its own artifacts, so a retried test contributes twice. The
  // total therefore matches what was actually stored, not the test count above it.
  //
  // Playwright names attachments 'screenshot' / 'video' / 'trace'; contentType is the
  // fallback for custom names (test.info().attach) that carry no recognizable name.
  private tallyArtifacts(attachments: TestEndEvent['attachments']): void {
    for (const a of attachments) {
      if ('unavailable' in a) {
        this.artifactCounts.unavailable++;
        continue;
      }

      const name = a.name.toLowerCase();
      const type = a.contentType.toLowerCase();

      if (name.includes('screenshot') || type.startsWith('image/')) {
        this.artifactCounts.screenshots++;
      } else if (name.includes('video') || type.startsWith('video/')) {
        this.artifactCounts.videos++;
      } else if (name.includes('trace') && type === 'application/zip') {
        this.artifactCounts.traces++;
      } else {
        this.artifactCounts.other++;
      }
    }
  }

  private attachmentsAsUnavailable(
    attachments: Array<{ name: string; contentType: string; path?: string; body?: Buffer }>
  ): TestEndEvent['attachments'] {
    return attachments
      .filter((a): a is { name: string; contentType: string; path: string } => !!a.path)
      .map((a) => ({ name: a.name, contentType: a.contentType, unavailable: true }));
  }

  private async uploadAttachments(
    attachments: Array<{ name: string; contentType: string; path?: string; body?: Buffer }>,
    testId: string
  ): Promise<TestEndEvent['attachments']> {
    // In-memory attachments (no path) are never uploaded.
    const fileCandidates = attachments.filter(
      (a): a is { name: string; contentType: string; path: string } => !!a.path
    );
    if (fileCandidates.length === 0) return [];

    if (this.isShuttingDown) {
      return fileCandidates.map((a) => ({ name: a.name, contentType: a.contentType, unavailable: true }));
    }

    // Uploads disabled or client not constructed → every file unavailable.
    // Never emit the local path.
    if (!this.artifactsEnabled || !this.artifactUploadClient) {
      return fileCandidates.map((a) => ({ name: a.name, contentType: a.contentType, unavailable: true }));
    }

    const stated = await this.statAttachments(fileCandidates);
    if (this.isShuttingDown) {
      return fileCandidates.map((a) => ({ name: a.name, contentType: a.contentType, unavailable: true }));
    }

    const { uploadable, preFiltered } = this.filterUploadable(stated);
    if (uploadable.length === 0) return preFiltered;

    const uploaded = await this.putBatch(uploadable, testId, this.artifactUploadClient);
    if (this.isShuttingDown) {
      return fileCandidates.map((a) => ({ name: a.name, contentType: a.contentType, unavailable: true }));
    }
    return [...preFiltered, ...uploaded];
  }

  private async statAttachments(
    files: Array<{ name: string; contentType: string; path: string }>
  ): Promise<StatedAttachment[]> {
    const results = await Promise.allSettled(files.map((f) => stat(f.path)));
    return files.map((f, i) => {
      const r = results[i];
      if (r.status === 'fulfilled') {
        return { name: f.name, contentType: f.contentType, path: f.path, sizeBytes: r.value.size };
      }
      this.log.warn(
        `Attachment '${f.name}' unavailable: ${r.reason instanceof Error ? r.reason.message : String(r.reason)}`
      );
      return { name: f.name, contentType: f.contentType, unavailable: true };
    });
  }

  private filterUploadable(stated: StatedAttachment[]): {
    uploadable: UploadableAttachment[];
    preFiltered: TestEndEvent['attachments'];
  } {
    const uploadable: UploadableAttachment[] = [];
    const preFiltered: TestEndEvent['attachments'] = [];
    for (const item of stated) {
      if ('unavailable' in item) {
        preFiltered.push({ name: item.name, contentType: item.contentType, unavailable: true });
        continue;
      }
      // Extension lives in the file path (e.g. test-failed-1.png), NOT the
      // Playwright attachment name ("screenshot"/"video"/"trace"), which has
      // none — checking the name would reject every real artifact.
      if (isAllowedExtension(basename(item.path)) && isWithinSizeLimit(item.sizeBytes)) {
        uploadable.push(item);
      } else {
        const reason = isAllowedExtension(basename(item.path))
          ? `${item.sizeBytes} bytes exceeds the size limit`
          : `extension not in the allowlist`;
        this.log.debug(`Attachment '${basename(item.path)}' skipped: ${reason}`);
        preFiltered.push({ name: item.name, contentType: item.contentType, unavailable: true });
      }
    }
    return { uploadable, preFiltered };
  }

  private async putBatch(
    uploadable: UploadableAttachment[],
    testId: string,
    client: ArtifactUploadClient
  ): Promise<TestEndEvent['attachments']> {
    const { ready, readFailed } = await this.snapshotAttachments(uploadable);
    if (ready.length === 0) return readFailed;

    const asUnavailable = (): TestEndEvent['attachments'] => [
      ...readFailed,
      ...ready.map((f) => ({ name: f.name, contentType: f.contentType, unavailable: true as const })),
    ];

    let tokens: UploadTokenResult;
    try {
      const request: UploadFileRequest[] = ready.map((f) => ({
        // Send the file's basename (with extension) — the server's allowlist and
        // the blob key's Content-Type (rsct) derive from the extension, which the
        // bare attachment name ("screenshot") lacks. The display name is kept
        // unchanged on the returned wire attachment below. sizeBytes is the
        // ACTUAL snapshot length, not stat — the server accounts on this value
        // and it must match the Content-Length on the PUT.
        name: basename(f.path),
        testId,
        contentType: f.contentType,
        sizeBytes: f.bytes.length,
      }));
      tokens = await client.requestUploadTokens(request);
    } catch (error) {
      const detail =
        error instanceof ArtifactUploadTokenError
          ? `${error.kind}${error.serverCode ? `/${error.serverCode}` : ''}: ${error.message}`
          : error instanceof Error
            ? error.message
            : String(error);
      this.log.warn(`Upload-token request failed for test ${testId}: ${detail}. Attachments marked unavailable.`);
      return asUnavailable();
    }

    // uploads is index-aligned with the request (data-handler contract:
    // handler.go iterates req.Files in order and appends to uploads).
    if (tokens.uploads.length !== ready.length) {
      this.log.warn(
        `Upload-token response length mismatch for test ${testId} (expected ${ready.length}, got ${tokens.uploads.length}). Attachments marked unavailable.`
      );
      return asUnavailable();
    }

    const settled = await Promise.allSettled(
      ready.map((f, i) => client.uploadFile(tokens.uploads[i].uploadUrl, f.bytes, f.contentType))
    );

    const uploaded = ready.map((f, i) => {
      const outcome = settled[i];
      if (outcome.status === 'fulfilled') {
        return { name: f.name, contentType: f.contentType, blobKey: tokens.uploads[i].blobKey };
      }
      this.log.warn(
        `Upload failed for '${f.name}' (test ${testId}): ${outcome.reason instanceof Error ? outcome.reason.message : String(outcome.reason)}`
      );
      return { name: f.name, contentType: f.contentType, unavailable: true as const };
    });

    return [...readFailed, ...uploaded];
  }

  private async snapshotAttachments(uploadable: UploadableAttachment[]): Promise<{
    ready: Array<{ name: string; contentType: string; path: string; sizeBytes: number; bytes: Buffer }>;
    readFailed: TestEndEvent['attachments'];
  }> {
    const reads = await Promise.allSettled(uploadable.map((f) => readFile(f.path)));
    const ready: Array<{ name: string; contentType: string; path: string; sizeBytes: number; bytes: Buffer }> = [];
    const readFailed: TestEndEvent['attachments'] = [];
    uploadable.forEach((f, i) => {
      const r = reads[i];
      if (r.status === 'fulfilled') {
        ready.push({ name: f.name, contentType: f.contentType, path: f.path, sizeBytes: f.sizeBytes, bytes: r.value });
      } else {
        this.log.warn(
          `Attachment '${f.name}' unavailable (snapshot failed): ${r.reason instanceof Error ? r.reason.message : String(r.reason)}`
        );
        readFailed.push({ name: f.name, contentType: f.contentType, unavailable: true });
      }
    });
    return { ready, readFailed };
  }

  private getProjectName(test: TestCase): string | undefined {
    let suite: Suite | undefined = test.parent;
    while (suite) {
      const project = suite.project();
      if (project) {
        return project.name || project.use?.defaultBrowserType || 'default';
      }
      suite = suite.parent;
    }
    return undefined;
  }
}
